// NerdBio core — pure genetics/molecular logic, no UI.
// Covered by tests/biotest.mjs against known sequences & ratios.
// NO medical/health calculation by design (scope decision, locked).

// ---------- Standard genetic code (DNA sense strand, T not U) ----------
// Verified against the NCBI standard table (translation table 1).
// Stop codons: TAA, TAG, TGA. Start: ATG (Met).
export const CODON_TABLE = {
  TTT: "F", TTC: "F", TTA: "L", TTG: "L",
  CTT: "L", CTC: "L", CTA: "L", CTG: "L",
  ATT: "I", ATC: "I", ATA: "I", ATG: "M",
  GTT: "V", GTC: "V", GTA: "V", GTG: "V",
  TCT: "S", TCC: "S", TCA: "S", TCG: "S",
  CCT: "P", CCC: "P", CCA: "P", CCG: "P",
  ACT: "T", ACC: "T", ACA: "T", ACG: "T",
  GCT: "A", GCC: "A", GCA: "A", GCG: "A",
  TAT: "Y", TAC: "Y", TAA: "*", TAG: "*",
  CAT: "H", CAC: "H", CAA: "Q", CAG: "Q",
  AAT: "N", AAC: "N", AAA: "K", AAG: "K",
  GAT: "D", GAC: "D", GAA: "E", GAG: "E",
  TGT: "C", TGC: "C", TGA: "*", TGG: "W",
  CGT: "R", CGC: "R", CGA: "R", CGG: "R",
  AGT: "S", AGC: "S", AGA: "R", AGG: "R",
  GGT: "G", GGC: "G", GGA: "G", GGG: "G",
};

export const AMINO_NAMES = {
  A: "Alanine", R: "Arginine", N: "Asparagine", D: "Aspartate",
  C: "Cysteine", E: "Glutamate", Q: "Glutamine", G: "Glycine",
  H: "Histidine", I: "Isoleucine", L: "Leucine", K: "Lysine",
  M: "Methionine", F: "Phenylalanine", P: "Proline", S: "Serine",
  T: "Threonine", W: "Tryptophan", Y: "Tyrosine", V: "Valine",
  "*": "STOP",
};

const cleanSeq = (s) => (s || "").toUpperCase().replace(/[^ACGTU]/g, "");

export function validateDNA(seq) {
  const c = (seq || "").toUpperCase().replace(/\s/g, "");
  if (!c) return { ok: false, error: "Sequence is empty" };
  const bad = c.match(/[^ACGT]/g);
  if (bad) return { ok: false, error: `Invalid DNA base(s): ${[...new Set(bad)].join(", ")}` };
  return { ok: true, seq: c };
}

// DNA -> mRNA (transcription of the template's complement convention:
// here we take the CODING/sense strand and just replace T with U, the
// standard intro-biology presentation).
export function transcribe(dna) {
  const v = validateDNA(dna);
  if (!v.ok) return v;
  return { ok: true, result: v.seq.replace(/T/g, "U") };
}

const COMP = { A: "T", T: "A", G: "C", C: "G" };
export function complement(dna) {
  const v = validateDNA(dna);
  if (!v.ok) return v;
  return { ok: true, result: v.seq.split("").map((b) => COMP[b]).join("") };
}
export function reverseComplement(dna) {
  const c = complement(dna);
  if (!c.ok) return c;
  return { ok: true, result: c.result.split("").reverse().join("") };
}

export function gcContent(dna) {
  const v = validateDNA(dna);
  if (!v.ok) return v;
  const gc = (v.seq.match(/[GC]/g) || []).length;
  return { ok: true, result: (gc / v.seq.length) * 100, gc, length: v.seq.length };
}

// Translate a DNA sense strand to protein. Reads frame 0 by default.
// Stops at first stop codon if stopAtStop=true.
export function translate(dna, { frame = 0, stopAtStop = true } = {}) {
  if (![0, 1, 2].includes(frame)) return { ok: false, error: "Frame must be 0, 1, or 2" };
  const v = validateDNA(dna);
  if (!v.ok) return v;
  const s = v.seq.slice(frame);
  const protein = [];
  for (let i = 0; i + 3 <= s.length; i += 3) {
    const codon = s.slice(i, i + 3);
    const aa = CODON_TABLE[codon];
    if (aa === "*") {
      if (stopAtStop) { protein.push("*"); break; }
      protein.push("*");
    } else protein.push(aa);
  }
  return { ok: true, result: protein.join(""), codons: Math.floor(s.length / 3) };
}

// ---------- Mendelian genetics ----------
// Punnett square for one gene. Parents like "Aa", "aa", "AA".
export function punnettMono(p1, p2) {
  const norm = (g) => (g || "").trim();
  const a = norm(p1), b = norm(p2);
  const sameGene = (g) => /^[A-Za-z]{2}$/.test(g) && g[0].toUpperCase() === g[1].toUpperCase();
  if (!sameGene(a) || !sameGene(b) || a[0].toUpperCase() !== b[0].toUpperCase())
    return { ok: false, error: "Use one gene pair, same letter, e.g. Aa × Aa" };
  const ga = a.split(""), gb = b.split("");
  const grid = [];
  const counts = {};
  for (const x of ga) {
    const row = [];
    for (const y of gb) {
      // order alleles: uppercase first, then alphabetical
      const geno = [x, y].sort((m, n) => {
        const mu = m === m.toUpperCase(), nu = n === n.toUpperCase();
        if (mu !== nu) return mu ? -1 : 1;
        return m.localeCompare(n);
      }).join("");
      row.push(geno);
      counts[geno] = (counts[geno] || 0) + 1;
    }
    grid.push(row);
  }
  // phenotype: dominant if any uppercase present
  const pheno = {};
  for (const [g, n] of Object.entries(counts)) {
    const dom = /[A-Z]/.test(g);
    const key = dom ? "dominant" : "recessive";
    pheno[key] = (pheno[key] || 0) + n;
  }
  return { ok: true, grid, genotypes: counts, phenotypes: pheno, total: 4 };
}

// Dihybrid (two independent genes), parents like "AaBb".
export function punnettDi(p1, p2) {
  const samePair = (g, i) => g[i].toUpperCase() === g[i + 1].toUpperCase();
  const validDi = (g) =>
    /^[A-Za-z]{4}$/.test(g) && samePair(g, 0) && samePair(g, 2) &&
    g[0].toUpperCase() !== g[2].toUpperCase();
  if (!validDi(p1) || !validDi(p2))
    return { ok: false, error: "Use two different gene pairs, e.g. AaBb × AaBb" };
  if (p1[0].toUpperCase() !== p2[0].toUpperCase() || p1[2].toUpperCase() !== p2[2].toUpperCase())
    return { ok: false, error: "Both parents must use the same two gene letters" };
  const gametes = (g) => {
    const a = [g[0], g[1]], b = [g[2], g[3]];
    const out = [];
    for (const x of a) for (const y of b) out.push(x + y);
    return out;
  };
  const sortGene = (x, y) =>
    [x, y].sort((m, n) => {
      const mu = m === m.toUpperCase(), nu = n === n.toUpperCase();
      if (mu !== nu) return mu ? -1 : 1;
      return m.localeCompare(n);
    }).join("");
  const g1 = gametes(p1), g2 = gametes(p2);
  const counts = {};
  for (const x of g1)
    for (const y of g2) {
      const geno = sortGene(x[0], y[0]) + sortGene(x[1], y[1]);
      counts[geno] = (counts[geno] || 0) + 1;
    }
  // phenotype ratio over 16 — labels derived from the ACTUAL gene letters
  const gene1 = p1[0].toUpperCase(), gene2 = p1[2].toUpperCase();
  const rec1 = gene1.toLowerCase() + gene1.toLowerCase();
  const rec2 = gene2.toLowerCase() + gene2.toLowerCase();
  const pheno = {};
  for (const [g, n] of Object.entries(counts)) {
    const t1 = /[A-Z]/.test(g.slice(0, 2)) ? `${gene1}_` : rec1;
    const t2 = /[A-Z]/.test(g.slice(2, 4)) ? `${gene2}_` : rec2;
    const key = `${t1} ${t2}`;
    pheno[key] = (pheno[key] || 0) + n;
  }
  return { ok: true, genotypes: counts, phenotypes: pheno, total: 16 };
}

// ---------- Hardy-Weinberg ----------
// Given allele freq p (and q=1-p) → genotype freqs. Or given q² (aa
// frequency) → p, q.
export function hardyWeinbergFromP(p) {
  if (!Number.isFinite(p) || p < 0 || p > 1)
    return { ok: false, error: "p must be a number between 0 and 1" };
  const q = 1 - p;
  const AA = p * p, Aa = 2 * p * q, aa = q * q;
  const r = (x) => Math.round(x * 1e6) / 1e6;
  const steps = [
    `q = 1 − p = 1 − ${r(p)} = ${r(q)}`,
    `AA = p² = ${r(p)}² = ${r(AA)}`,
    `Aa = 2pq = 2 × ${r(p)} × ${r(q)} = ${r(Aa)}`,
    `aa = q² = ${r(q)}² = ${r(aa)}`,
    `check: p² + 2pq + q² = ${r(AA)} + ${r(Aa)} + ${r(aa)} = ${r(AA + Aa + aa)}`,
  ];
  return { ok: true, p, q, AA, Aa, aa, steps };
}
export function hardyWeinbergFromAa(aaFreq) {
  if (!Number.isFinite(aaFreq) || aaFreq < 0 || aaFreq > 1)
    return { ok: false, error: "aa frequency must be a number between 0 and 1" };
  const q = Math.sqrt(aaFreq);
  const p = 1 - q;
  const AA = p * p, Aa = 2 * p * q, aa = q * q;
  const r = (x) => Math.round(x * 1e6) / 1e6;
  const steps = [
    `q = √(aa) = √${r(aaFreq)} = ${r(q)}`,
    `p = 1 − q = 1 − ${r(q)} = ${r(p)}`,
    `AA = p² = ${r(p)}² = ${r(AA)}`,
    `Aa = 2pq = 2 × ${r(p)} × ${r(q)} = ${r(Aa)}`,
    `aa = q² = ${r(q)}² = ${r(aa)}`,
    `check: p² + 2pq + q² = ${r(AA)} + ${r(Aa)} + ${r(aa)} = ${r(AA + Aa + aa)}`,
  ];
  return { ok: true, p, q, AA, Aa, aa, steps };
}

// ---------- Lab molarity / dilution ----------
// molarity = moles / liters ; solve missing one.
export function molarity({ moles, liters, molarityVal }) {
  const vals = [moles, liters, molarityVal];
  const finite = (x) => x == null || Number.isFinite(x);
  const nonneg = (x) => x == null || x >= 0;
  if (!vals.every(finite)) return { ok: false, error: "Values must be finite numbers" };
  if (!vals.every(nonneg)) return { ok: false, error: "Moles, volume, and molarity cannot be negative" };
  const have = vals.filter((x) => x != null && !isNaN(x)).length;
  if (have < 2) return { ok: false, error: "Provide two of: moles, liters, molarity" };
  if (molarityVal == null) {
    if (liters === 0) return { ok: false, error: "Volume cannot be 0" };
    return { ok: true, result: { moles, liters, molarityVal: moles / liters }, solvedFor: "molarity" };
  }
  if (moles == null) return { ok: true, result: { moles: molarityVal * liters, liters, molarityVal }, solvedFor: "moles" };
  if (molarityVal === 0) return { ok: false, error: "Molarity cannot be 0" };
  return { ok: true, result: { moles, liters: moles / molarityVal, molarityVal }, solvedFor: "liters" };
}
// C1V1 = C2V2 — solve the one missing.
export function dilution({ c1, v1, c2, v2 }) {
  const vals = { c1, v1, c2, v2 };
  const list = [c1, v1, c2, v2];
  const finite = (x) => x == null || Number.isFinite(x);
  const nonneg = (x) => x == null || x >= 0;
  if (!list.every(finite)) return { ok: false, error: "Values must be finite numbers" };
  if (!list.every(nonneg)) return { ok: false, error: "Concentrations and volumes cannot be negative" };
  const missing = Object.keys(vals).filter((k) => vals[k] == null || isNaN(vals[k]));
  if (missing.length !== 1) return { ok: false, error: "Provide exactly three values" };
  const m = missing[0];
  let r;
  if (m === "c1") { if (v1 === 0) return { ok: false, error: "V1 cannot be 0" }; r = (c2 * v2) / v1; }
  if (m === "v1") { if (c1 === 0) return { ok: false, error: "C1 cannot be 0" }; r = (c2 * v2) / c1; }
  if (m === "c2") { if (v2 === 0) return { ok: false, error: "V2 cannot be 0" }; r = (c1 * v1) / v2; }
  if (m === "v2") { if (c2 === 0) return { ok: false, error: "C2 cannot be 0" }; r = (c1 * v1) / c2; }
  return { ok: true, solvedFor: m, result: r };
}

export function round(x, p = 6) {
  if (typeof x !== "number" || !isFinite(x)) return x;
  const f = Math.pow(10, p);
  return Math.round(x * f) / f;
}

